/**
 * AIVA-8913 — DI-02 for the model: what the `ppt_get_speaker_notes` tool returns
 * when the add-in's notes regex missed and the prompt reached `ppt_general`.
 *
 * Pure function over the same deck scan the notes card uses. The G-7 outcome
 * (none set / none readable / partial) comes from buildSpeakerNotesResult, so
 * the model and the card can never disagree about it. Unlike the card, each
 * slide's notes go in full, marked "--- Slide N notes ---", cut at a slide
 * boundary under DECK_CONTEXT_CHAR_CAP like the deck read.
 */

import { DECK_CONTEXT_CHAR_CAP } from "../../../const";
import type { SlideNotes, SpeakerNotesScan } from "../types";
import { buildSpeakerNotesResult } from "./buildSpeakerNotesResult";

function notesBlock(slide: SlideNotes): string {
  return `--- Slide ${slide.slideNumber} notes ---\n${slide.text.trim()}`;
}

/** The notes text, cut at a slide boundary; an oversized first block is clipped. */
function joinNotes(notes: readonly SlideNotes[], cap: number): { text: string; included: number; truncated: boolean } {
  const blocks: string[] = [];
  let length = 0;
  for (const slide of notes) {
    const block = notesBlock(slide);
    const added = (blocks.length > 0 ? 2 : 0) + block.length;
    if (length + added > cap) {
      if (blocks.length === 0) blocks.push(block.slice(0, cap));
      return { text: blocks.join("\n\n"), included: blocks.length, truncated: true };
    }
    blocks.push(block);
    length += added;
  }
  return { text: blocks.join("\n\n"), included: blocks.length, truncated: false };
}

export function buildSpeakerNotesToolResult(
  scan: SpeakerNotesScan | null,
  cap = DECK_CONTEXT_CHAR_CAP,
): Record<string, unknown> {
  const result = buildSpeakerNotesResult(scan);
  if (!scan || result.outcome === "none_readable") return { readOk: false, message: result.header };

  const counts = {
    totalSlides: scan.totalSlides,
    slidesWithNotes: scan.notes.length,
    unreadableCount: scan.unreadableCount,
  };
  if (result.outcome !== "has_notes") {
    // No notes to send: the header is the approved empty-state copy.
    const message = result.footnote ? `${result.header} ${result.footnote}` : result.header;
    return { readOk: true, ...counts, text: "", truncated: false, message };
  }

  const { text, included, truncated } = joinNotes(scan.notes, cap);
  return {
    readOk: true,
    ...counts,
    text,
    slidesWithNotesIncluded: included,
    truncated,
    ...(result.footnote ? { message: result.footnote } : {}),
  };
}
